import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/clientWeb/components/ui/dialog'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { useDevicesStore } from '@/features/devices/store/devices.store'
import { usePermissionWatcher } from '../hooks/usePermissionWatcher'
import { recoverDeviceNow } from '../services/device-permission-recovery'

/**
 * The meeting's answer to a microphone or camera grant being revoked in the
 * browser WHILE the call is running.
 *
 * Mounted once inside the meeting. It runs the Permissions watcher (which, with
 * the device manager's `NotAllowedError` path, turns the device off and arms
 * recovery on a revoke) and renders the centered dialog that replaces the old
 * corner toast.
 *
 * It LEADS WITH THE REQUEST, like Google Meet / Zoom: the primary button fires a
 * fresh `getUserMedia`, which brings the browser's own permission popup back up
 * whenever the state allows it (`prompt` — e.g. after a Reset, or a device never
 * decided) — one click, no reload. The manual address-bar steps appear only
 * AFTER a click confirms a hard `denied` block, because once a site is Blocked
 * no script can re-prompt it (a browser rule Meet and Zoom hit too); the
 * `change` watcher then recovers the device on its own when the user flips it.
 */
export function MeetingPermissionRecovery() {
  usePermissionWatcher()

  const t = useTranslation()
  const kind = useDevicesStore((s) => s.recoveryDialogKind)
  const dismiss = useDevicesStore((s) => s.dismissPermissionRecoveryDialog)
  const [busy, setBusy] = useState(false)
  const [blockedAfterTry, setBlockedAfterTry] = useState(false)

  // Start every new dialog in "ask" mode — lead with the request, not the steps.
  useEffect(() => {
    setBlockedAfterTry(false)
  }, [kind])

  if (!kind) return null

  const isAudio = kind === 'audio'
  const copy = isAudio
    ? {
        title: 'dashboard.micAccessRevoked',
        desc: 'dashboard.micAccessRevokedDesc',
        toEnable: 'dashboard.toEnableMicAccess',
        find: 'dashboard.findMicPermission',
        allow: 'dashboard.allowMicAgain',
      }
    : {
        title: 'dashboard.cameraAccessRevoked',
        desc: 'dashboard.cameraAccessRevokedDesc',
        toEnable: 'dashboard.toEnableCameraAccess',
        find: 'dashboard.findCameraPermission',
        allow: 'dashboard.allowCameraAgain',
      }

  const onPrimary = async () => {
    if (busy) return
    setBusy(true)
    try {
      // Fires getUserMedia when the state allows it -> the native popup. On
      // success recoverDeviceNow clears recovery and the dialog closes. If the
      // browser refuses (hard Block, no popup possible), reveal the steps.
      const ok = await recoverDeviceNow(kind)
      if (!ok) setBlockedAfterTry(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        // Closing means "not now" — hide the dialog but keep recovery armed, so
        // the device still comes back on its own once access is restored.
        if (!open) dismiss()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(copy.title)}</DialogTitle>
          <DialogDescription>{t(copy.desc)}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {blockedAfterTry && (
            // Only reached once the browser has refused an in-dialog re-prompt:
            // the site is Blocked and no script can pop the native dialog. No
            // "refresh the page" step — recovery is live once they flip it.
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-600 text-xs font-bold">!</span>
                </div>
                <div className="text-sm text-amber-800">
                  <p className="font-medium">{t(copy.toEnable)}</p>
                  <ol className="mt-1 list-decimal list-inside space-y-1 text-xs">
                    <li>{t('dashboard.clickLockIcon')}</li>
                    <li>{t(copy.find)}</li>
                    <li>{t('dashboard.changeBlockToAllow')}</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={dismiss}
              className="flex-1 rounded-lg border border-slate-200 dark:border-border px-4 py-2 text-sm font-medium text-slate-700 dark:text-foreground hover:bg-slate-50 dark:hover:bg-muted transition"
            >
              {t('dashboard.illDoItLater')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onPrimary()}
              className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {blockedAfterTry ? t('dashboard.tryAgain') : t(copy.allow)}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
